import { trpc } from "@client/trpc";
import { Button, Skeleton } from "@sfc/ui";
import { Link } from "wouter";

const emptyApplications = (
  <section className="space-y-3">
    <h2 className="heading-md">Your Applications</h2>
    <p className="body-md text-muted-foreground">
      You haven’t started an application yet.
    </p>
    <Link
      href="/apply"
      className="body-md text-primary underline underline-offset-4"
    >
      Explore funding rounds →
    </Link>
  </section>
);

export function HomeApplications({
  viewerId,
  enabled,
  showEmpty = false,
}: {
  viewerId: string;
  enabled: boolean;
  showEmpty?: boolean;
}) {
  if (!enabled)
    return showEmpty ? (
      <section className="space-y-3">
        <h2 className="heading-md">Your workspace</h2>
        <p className="body-md text-muted-foreground">
          Your funding rounds will appear here when you join one.
        </p>
      </section>
    ) : null;
  return (
    <ApplicationList key={viewerId} viewerId={viewerId} showEmpty={showEmpty} />
  );
}

function ApplicationList({
  viewerId,
  showEmpty,
}: {
  viewerId: string;
  showEmpty: boolean;
}) {
  const query = trpc.getMyApplications.useQuery(undefined, { gcTime: 0 });
  const applications =
    query.data?.viewerId === viewerId ? query.data.applications : undefined;
  if (applications?.length === 0) return showEmpty ? emptyApplications : null;
  return (
    <section aria-labelledby="your-applications-heading" className="space-y-4">
      <h2 id="your-applications-heading" className="heading-md">
        Your Applications
      </h2>
      <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
        {query.isError ? (
          <div role="alert" className="p-6">
            <p>Couldn’t load your applications.</p>
            <Button variant="nav" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </div>
        ) : applications === undefined ? (
          <Skeleton
            className="h-28 w-full"
            role="status"
            aria-label="Loading applications"
          />
        ) : (
          applications.map((application) => (
            <article
              key={application.id}
              className="flex flex-wrap items-center gap-4 p-5"
            >
              <div className="min-w-0 flex-1 basis-64">
                <h3 className="heading-sm [overflow-wrap:anywhere]">
                  {application.roundName}
                </h3>
                <p className="mt-2 body-sm text-muted-foreground">
                  Team:{" "}
                  <Link
                    className="underline underline-offset-4"
                    href={`/teams/${application.team.id}`}
                  >
                    {application.team.name}
                  </Link>
                </p>
              </div>
              <span
                className={
                  application.status === "SUBMITTED"
                    ? "body-sm font-medium text-success"
                    : "body-sm font-medium text-muted-foreground"
                }
              >
                {application.status === "SUBMITTED" ? "Submitted" : "Draft"}
              </span>
              {
                <Button
                  variant="nav"
                  className="text-primary"
                  nativeButton={false}
                  render={
                    <Link
                      href={`/applications/${encodeURIComponent(application.id)}`}
                    />
                  }
                >
                  {application.status === "SUBMITTED" || !application.canOpen
                    ? "View application →"
                    : "Continue application →"}
                </Button>
              }
            </article>
          ))
        )}
      </div>
    </section>
  );
}
